
import React, { useEffect, useRef } from 'react';
import { Film, Share2 } from 'lucide-react';
import ServiceCard from './ServiceCard';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { Link } from 'react-router-dom';
import { useNavigationUtils } from '@/hooks/useNavigationUtils';

const Services = () => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];
  const { getPricingPath } = useNavigationUtils();
  const sectionRef = useRef<HTMLElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const cardElements = entry.target.querySelectorAll('.animate-on-scroll');
          cardElements.forEach((el, index) => {
            setTimeout(() => {
              el.classList.add('animate-fade-in');
            }, index * 150);
          });
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2 });
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const services = [
    {
      title: t.productVideos,
      description: t.productVideosDesc,
      icon: Film,
      link: '/services/product-videos'
    },
    {
      title: t.socialMediaAds,
      description: t.socialMediaAdsDesc,
      icon: Share2,
      link: '/services/social-media-ads'
    }
  ];

  return (
    <section 
      ref={sectionRef} 
      className="py-16 md:py-24 bg-gradient-to-b from-[#0B0B0B] to-[#101010] relative overflow-hidden" 
      id="services"
    >
      {/* Dynamic animated particles background */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {/* Flowing data particles */}
        <div className="absolute inset-0 opacity-10">
          {Array(15).fill(0).map((_, i) => (
            <div 
              key={i}
              className="absolute rounded-full bg-gradient-to-r from-purple-500/40 to-cyan-400/40 blur-xl"
              style={{
                width: Math.random() * 200 + 50 + 'px',
                height: Math.random() * 200 + 50 + 'px',
                left: Math.random() * 100 + '%',
                top: Math.random() * 100 + '%',
                opacity: Math.random() * 0.3 + 0.2,
                animation: `parallax-shift ${Math.random() * 20 + 10}s ease-in-out infinite ${Math.random() * 5}s`,
                transform: `scale(${Math.random() * 0.5 + 0.5})`,
              }}
            />
          ))}
        </div>
        
        {/* Neural network lines/Digital waves */}
        <div className="absolute inset-0 opacity-15">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="networkGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#9333EA" stopOpacity="0.6"/>
                <stop offset="100%" stopColor="#06B6D4" stopOpacity="0.4"/>
              </linearGradient>
            </defs>
            {Array(10).fill(0).map((_, i) => (
              <path 
                key={i} 
                d={`M${Math.random() * 100},${Math.random() * 100} Q${Math.random() * 100},${Math.random() * 100} ${Math.random() * 100},${Math.random() * 100}`}
                stroke="url(#networkGradient)"
                strokeWidth="0.5"
                fill="none"
                className="svg-drawing-animation"
              />
            ))}
          </svg>
        </div>
        
        {/* Center vertical glow */}
        <div className="absolute left-1/2 top-0 bottom-0 w-[60%] -translate-x-1/2 bg-gradient-to-t from-purple-900/10 via-purple-500/15 to-purple-900/10 blur-3xl pointer-events-none"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16 animate-on-scroll">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 font-future tracking-wider text-white micro-glitch">
            {t.ourServices}
          </h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            {t.servicesSubtitle}
          </p>
          <div className="w-3 h-3 rounded-full bg-purple-500 mx-auto mt-6 animate-pulse"></div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {services.map((service, index) => (
            <div 
              key={index} 
              className="animate-on-scroll" 
              style={{ 
                animationDelay: `${index * 0.15}s`,
                opacity: 1,
                transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
              }}
            >
              <ServiceCard
                title={service.title}
                description={service.description}
                icon={service.icon}
                link={service.link}
              />
            </div>
          ))}
        </div>

        {/* Pricing CTA Section */}
        <div className="max-w-3xl mx-auto mt-20 animate-on-scroll">
          <div className="bg-gradient-to-r from-purple-900/30 to-indigo-900/30 backdrop-blur-md border border-purple-500/30 rounded-xl p-8 text-center">
            <h3 className="text-2xl font-semibold mb-4 font-future tracking-wide text-white">Transparente Preise für Ihre Video-Projekte</h3>
            <p className="text-gray-300 mb-6">
              Entdecken Sie unsere verschiedenen Preispakete für KI-Produktvideos und Social Media Inhalte - von Einzel-Projekten bis zu monatlichen Abonnements.
            </p>
            <Link to={getPricingPath()}>
              <button className="px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-md hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 font-medium">
                {t.pricing}
              </button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
